import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Calendar, Clock, User, Tag, Share2, BookmarkPlus } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NewsArticle = () => {
  const { articleId } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setLoading(true);
        
        // Fetch the specific article
        const articleResponse = await axios.get(`${BACKEND_URL}/api/news/${articleId}`);
        const articleData = articleResponse.data;
        
        // Format the article data
        const formattedArticle = {
          id: articleData._id || articleData.id,
          title: articleData.title,
          content: articleData.content || [],
          excerpt: articleData.excerpt,
          image: articleData.image,
          author: articleData.author,
          date: new Date(articleData.created_at || articleData.updated_at).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          readTime: `${Math.max(1, Math.ceil((articleData.content || []).join(' ').split(' ').length / 200))} min read`,
          category: articleData.category,
          tags: articleData.tags || []
        };
        
        setArticle(formattedArticle);
        
        // Fetch related articles (other published articles)
        const relatedResponse = await axios.get(`${BACKEND_URL}/api/news/`, {
          params: { published_only: true }
        });
        const relatedData = relatedResponse.data
          .filter(item => (item._id || item.id) !== articleId)
          .slice(0, 3)
          .map(item => ({
            id: item._id || item.id,
            title: item.title,
            excerpt: item.excerpt,
            image: item.image,
            date: new Date(item.created_at || item.updated_at).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long', 
              day: 'numeric'
            }),
            category: item.category
          }));
        
        setRelatedArticles(relatedData);
        
      } catch (err) {
        console.error('Error fetching article:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    if (articleId) {
      fetchArticle();
    }
  }, [articleId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vincotek-blue mx-auto mb-4"></div>
            <p className="text-gray-600">Loading article...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl font-bold text-vincotek-dark mb-6">Article Not Found</h1>
            <p className="text-xl text-gray-600 mb-8">
              Sorry, the article you're looking for doesn't exist or has been removed.
            </p>
            <Link to="/news" className="inline-flex items-center">
              <Button className="bg-vincotek-blue hover:bg-blue-600 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg">
                <ArrowLeft className="mr-2 h-5 w-5" />
                Back to News
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Article data is now fetched from API instead of hardcoded

  // Use the fetched article data instead of hardcoded articles
  const currentArticle = article;

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/news">
          <Button variant="outline" className="mb-8 text-vincotek-blue border-vincotek-blue hover:bg-vincotek-blue hover:text-white rounded-full transition-all duration-300 group">
            <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
            Back to News
          </Button>
        </Link>
      </div>

      {/* Article Header */}
      <section className="pb-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-6">
              <div className="flex items-center space-x-1">
                <Tag className="h-4 w-4" />
                <span className="bg-vincotek-blue text-white px-3 py-1 rounded-full text-xs font-medium">
                  {currentArticle.category}
                </span>
              </div>
              <div className="flex items-center space-x-1">
                <Calendar className="h-4 w-4" />
                <span>{currentArticle.date}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Clock className="h-4 w-4" />
                <span>{currentArticle.readTime}</span>
              </div>
              <div className="flex items-center space-x-1">
                <User className="h-4 w-4" />
                <span>By {currentArticle.author}</span>
              </div>
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-bold text-vincotek-dark leading-tight mb-8">
              {currentArticle.title}
            </h1>

            {/* Featured Image */}
            <div className="relative mb-12">
              <img 
                src={currentArticle.image} 
                alt={currentArticle.title}
                className="w-full h-96 object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute top-4 right-4">
                <Button variant="outline" className="bg-white/90 backdrop-blur-sm border-white text-vincotek-dark hover:bg-white rounded-full">
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </Button>
              </div>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-8">
              {currentArticle.tags.map((tag, index) => (
                <span
                  key={index}
                  className="bg-gradient-to-r from-vincotek-yellow/20 to-vincotek-blue/20 text-vincotek-dark px-3 py-1 rounded-full text-sm font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none">
              {currentArticle.content.map((paragraph, index) => (
                <p key={index} className="text-lg text-gray-700 leading-relaxed mb-6">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Articles */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-vincotek-dark mb-12 text-center">
              Related Articles
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedArticles.map((article) => (
                <Link key={article.id} to={`/news/${article.id}`}>
                  <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white border-0 overflow-hidden">
                    <div className="relative overflow-hidden">
                      <img 
                        src={article.image} 
                        alt={article.title}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-white/90 backdrop-blur-sm text-vincotek-dark px-3 py-1 rounded-full text-xs font-medium">
                          {article.category}
                        </span>
                      </div>
                    </div>
                    
                    <CardContent className="p-6">
                      <div className="flex items-center space-x-4 text-xs text-gray-500 mb-3">
                        <div className="flex items-center space-x-1">
                          <Calendar className="h-3 w-3" />
                          <span>{article.date}</span>
                        </div>
                        <span>{article.readTime}</span>
                      </div>
                      
                      <h3 className="text-lg font-semibold text-vincotek-dark mb-3 line-clamp-2 leading-tight">
                        {article.title}
                      </h3>
                      
                      <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">
                        {article.excerpt}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewsArticle;